import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

// Icons
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import VideoCallIcon from '@mui/icons-material/VideoCall';

// Import logo
import logo0 from '../assets/logo0.png';
import logoWhite from '../assets/logo-white.png'; // Import white version of logo if available

const RepairGuide = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  
  // Sample repair steps
  const steps = [
    {
      label: 'Use AI Diagnostics',
      description: 'Start by using our AI Diagnostic tool to accurately identify your device issue.',
      tips: ['Take clear photos of the device problem', 'Describe symptoms in detail for better AI analysis', 'Follow the AI recommendations for next steps'],
      action: {
        text: 'Go to AI Diagnostics',
        path: '/ai-diagnostics'
      }
    },
    {
      label: 'Schedule Video Consultation',
      description: 'Connect with a certified technician via video call for personalized guidance before attempting repairs.',
      tips: ['Prepare your device for the call', 'Have good lighting for better visibility', 'Take notes during the consultation'],
      action: {
        text: 'Book Video Consultation',
        path: '/consultation'
      }
    },
    {
      label: 'Prepare Your Workspace',
      description: 'Set up a clean, well-lit workspace with all your tools ready. Place a soft mat or towel to prevent scratches.',
      tips: ['Take a photo before disassembly', 'Keep small containers for screws', 'Follow the technician\'s recommendations from your consultation']
    },
    {
      label: 'Power Off and Remove SIM Card',
      description: 'Power off your device completely. Use the SIM ejector tool to remove the SIM card tray if applicable.',
      tips: ['Hold the power button until "slide to power off" appears', 'Store the SIM card in a safe place']
    },
    {
      label: 'Remove External Screws',
      description: 'Using the appropriate screwdriver, remove the external screws. Note that these screws may be different lengths.',
      tips: ['Keep track of which screw came from which location', 'Use a magnetic screwdriver to avoid losing screws'],
      warnings: ['Do not force screws if they are tight - ensure you are using the correct size screwdriver']
    },
    {
      label: 'Open the Device',
      description: 'Insert a plastic opening tool between the screen and body. Gently pry upward to separate the components.',
      tips: ['Work slowly around the edges', 'Do not open completely if components are still connected by cables'],
      warnings: ['Do not use excessive force or metal tools that could damage the screen or frame']
    },
    {
      label: 'Disconnect the Battery',
      description: 'Locate the battery connector on the logic board. Use a plastic tool to gently disconnect it.',
      tips: ['This prevents any short circuits while working', 'Do not pull on the cables, only the connector'],
      warnings: ['Never use metal tools when disconnecting connectors']
    },
    {
      label: 'Complete the Repair',
      description: 'Perform the specific repair needed for your device issue, following the guidance from your AI diagnosis and video consultation.',
      tips: ['Follow manufacturer guidelines when available', 'Take your time with delicate components', 'Refer to your consultation notes if you get stuck']
    },
    {
      label: 'Reassemble the Device',
      description: 'Carefully reassemble all components in the reverse order of disassembly.',
      tips: ['Ensure no cables are pinched', 'Make sure all connectors are properly seated']
    },
    {
      label: 'Test Your Device',
      description: 'Power on your device and test that the issue has been resolved.',
      tips: ['Test all functions that were affected by the issue', 'Allow the device to run for a few minutes before final assessment']
    },
    {
      label: 'Follow-up Consultation',
      description: 'If you encounter any issues after repair, schedule a follow-up video consultation for additional support.',
      tips: ['Document any new issues that arise', 'Be prepared to show the device during the call'],
      action: {
        text: 'Schedule Follow-up',
        path: '/consultation'
      }
    }
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <Box sx={{ bgcolor: '#111', minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ bgcolor: '#000', boxShadow: 'none', borderBottom: '1px solid #333' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src={logoWhite || logo0} 
              alt="Logo" 
              style={{ 
                height: '30px',
                filter: logoWhite ? 'none' : 'brightness(0) invert(1)' // Apply filter if using original logo
              }} 
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              component={RouterLink} 
              to="/" 
              startIcon={<HomeIcon />}
              sx={{ color: '#FFC107' }}
            >
              Home
            </Button>
            <Button 
              component={RouterLink} 
              to="/mission" 
              startIcon={<InfoIcon />}
              sx={{ color: '#FFC107' }}
            >
              Our Mission
            </Button>
            <Button 
              component={RouterLink} 
              to="/bedoconsulation" 
              startIcon={<VideoCallIcon />}
              sx={{ 
                bgcolor: '#FF9800',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#F57C00'
                }
              }}
            >
              Book Consultation
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Header */}
        <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography 
            variant="h3" 
            component="h1" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              color: '#FF9800',
              mb: 2
            }}
          >
            Device Repair Guide
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            color="text.secondary"
            sx={{ 
              maxWidth: '800px',
              mb: 4,
              color: '#e0e0e0'
            }}
          >
            Follow our step-by-step instructions to repair your device safely and effectively
          </Typography>
          
          {/* Quick Navigation Buttons */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mb: 4 }}>
            <Button 
              variant="contained" 
              onClick={() => navigate('/ai-diagnostics')}
              sx={{ 
                bgcolor: '#FF9800',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#F57C00'
                }
              }}
            >
              AI Diagnostics
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate('/consultation')}
              sx={{ 
                bgcolor: '#FF9800',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#F57C00'
                }
              }}
            >
              Video Consultation
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/bedoconsulation')}
              sx={{ 
                borderColor: '#FF9800',
                color: '#FF9800',
                '&:hover': {
                  borderColor: '#F57C00',
                  bgcolor: 'rgba(255, 152, 0, 0.1)'
                }
              }}
            >
              Book Expert
            </Button>
          </Box>
        </Box>

        {/* Main Content */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ 
              p: 4, 
              borderRadius: '8px', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              bgcolor: '#222',
              border: '1px solid #333'
            }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#FF9800', mb: 3 }}>
                iPhone Battery Connection Repair
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TimerIcon sx={{ color: '#FFC107', mr: 1 }} />
                  <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                    <strong>Time Required:</strong> 15-30 minutes
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ color: '#FFC107', mr: 1 }} />
                  <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                    <strong>Difficulty:</strong> Medium
                  </Typography>
                </Box>
              </Box>
              
              <Alert severity="warning" sx={{ 
                mb: 4,
                bgcolor: 'rgba(255, 152, 0, 0.15)',
                color: '#FFC107',
                '& .MuiAlert-icon': {
                  color: '#FFC107'
                }
              }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Safety Warning
                </Typography>
                <Typography variant="body2">
                  Always power off your device and disconnect the battery before performing any repairs. 
                  Handle components with care and avoid using excessive force.
                </Typography>
              </Alert>
              
              <Stepper activeStep={activeStep} orientation="vertical" sx={{
                '& .MuiStepConnector-line': {
                  borderColor: '#FF9800'
                }
              }}>
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel
                      StepIconProps={{
                        sx: {
                          color: activeStep === index ? '#FF9800' : '#555',
                          '&.Mui-completed': {
                            color: '#FF9800'
                          }
                        }
                      }}
                    >
                      <Typography fontWeight="bold" sx={{ color: '#FFC107' }}>{step.label}</Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography sx={{ color: '#e0e0e0' }}>{step.description}</Typography>
                      
                      {step.tips && (
                        <Box sx={{ mt: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', p: 1.5, borderLeft: '3px solid #FF9800' }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#FFC107' }}>
                            Tips:
                          </Typography>
                          <List dense disablePadding>
                            {step.tips.map((tip, i) => (
                              <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <CheckCircleIcon fontSize="small" sx={{ color: '#FFC107' }} />
                                </ListItemIcon>
                                <ListItemText primary={tip} sx={{ '& .MuiListItemText-primary': { color: '#e0e0e0' } }} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      
                      {step.warnings && (
                        <Box sx={{ mt: 2, bgcolor: 'rgba(255, 87, 34, 0.1)', p: 1.5, borderLeft: '3px solid #FF5722' }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#FF5722' }}>
                            Warnings:
                          </Typography>
                          <List dense disablePadding>
                            {step.warnings.map((warning, i) => (
                              <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <WarningIcon fontSize="small" sx={{ color: '#FF5722' }} />
                                </ListItemIcon>
                                <ListItemText primary={warning} sx={{ '& .MuiListItemText-primary': { color: '#e0e0e0' } }} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      
                      {step.action && (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            onClick={() => navigate(step.action.path)}
                            sx={{ 
                              bgcolor: '#FF9800',
                              '&:hover': {
                                bgcolor: '#F57C00'
                              }
                            }}
                          >
                            {step.action.text}
                          </Button>
                        </Box>
                      )}
                      
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <div>
                          <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            sx={{ mr: 1, color: '#e0e0e0', borderColor: '#444', '&:hover': { borderColor: '#FF9800' } }}
                            variant="outlined"
                          >
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleNext}
                            sx={{ 
                              bgcolor: '#FF9800',
                              '&:hover': {
                                bgcolor: '#F57C00'
                              }
                            }}
                          >
                            {activeStep === steps.length - 1 ? 'Finish' : 'Continue'}
                          </Button>
                        </div>
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
              {activeStep === steps.length && (
                <Paper square elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 152, 0, 0.1)', mt: 2, border: '1px solid #FF9800' }}>
                  <Typography sx={{ color: '#FFC107', fontWeight: 'bold' }}>All steps completed - you're finished!</Typography>
                  <Button onClick={handleReset} sx={{ mt: 1, mr: 1, bgcolor: '#FF9800', color: '#fff', '&:hover': { bgcolor: '#F57C00' } }}>
                    Reset
                  </Button>
                </Paper>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: '8px', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              bgcolor: '#222',
              border: '1px solid #333',
              height: '100%'
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#FF9800', mb: 3 }}>
                Tools You'll Need
              </Typography>
              
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <BuildIcon sx={{ color: '#FFC107' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Precision Screwdriver Set" 
                    secondary="For removing small screws" 
                    sx={{ 
                      '& .MuiListItemText-primary': { color: '#e0e0e0' },
                      '& .MuiListItemText-secondary': { color: '#aaa' }
                    }}
                  />
                </ListItem>
                <Divider sx={{ bgcolor: '#444' }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <BuildIcon sx={{ color: '#FFC107' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Plastic Opening Tools" 
                    secondary="To safely pry open device casings" 
                    sx={{ 
                      '& .MuiListItemText-primary': { color: '#e0e0e0' },
                      '& .MuiListItemText-secondary': { color: '#aaa' }
                    }}
                  />
                </ListItem>
                <Divider sx={{ bgcolor: '#444' }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <BuildIcon sx={{ color: '#FFC107' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Spudger" 
                    secondary="For disconnecting cables and connectors" 
                    sx={{ 
                      '& .MuiListItemText-primary': { color: '#e0e0e0' },
                      '& .MuiListItemText-secondary': { color: '#aaa' }
                    }}
                  />
                </ListItem>
                <Divider sx={{ bgcolor: '#444' }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <BuildIcon sx={{ color: '#FFC107' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Tweezers" 
                    secondary="For handling small components" 
                    sx={{ 
                      '& .MuiListItemText-primary': { color: '#e0e0e0' },
                      '& .MuiListItemText-secondary': { color: '#aaa' }
                    }}
                  />
                </ListItem>
                <Divider sx={{ bgcolor: '#444' }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <BuildIcon sx={{ color: '#FFC107' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Anti-Static Wrist Strap" 
                    secondary="To prevent static damage to components" 
                    sx={{ 
                      '& .MuiListItemText-primary': { color: '#e0e0e0' },
                      '& .MuiListItemText-secondary': { color: '#aaa' }
                    }}
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                  Need Help?
                </Typography>
                <Typography variant="body2" paragraph sx={{ color: '#e0e0e0' }}>
                  If you're stuck or unsure about any step, our experts are ready to help.
                </Typography>
                <Button 
                  variant="contained" 
                  fullWidth
                  startIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/consultation')}
                  sx={{ 
                    bgcolor: '#FF9800',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: '#F57C00'
                    },
                    mt: 2
                  }}
                >
                  Get Expert Help
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default RepairGuide; 